import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import PersonalInformation from './PersonalInformation.js';
import Education from './Education.js';
import ProgramOfInterest from './ProgramOfInterest.js';
import Demographics from './Demographics.js';
import Goals from './Goals.js';
import Field from '../Field.js';
import { FieldChoiceGroup } from '../FieldGroup.js';
import Button from '@helios/Button';
import FormActions from '@helios/FormActions';
import Spinner from '@helios/Spinner';
import reviewStyles from './ApplicationFormReview.module.scss';
import styles from './ApplicationForm.module.scss';
const PERSONAL_INFORMATION = 'Personal Information';
const EDUCATION = 'Education';
const PROGRAM_OF_INTEREST = 'Program of Interest';
const DEMOGRAPHICS = 'Demographics';
const YOUR_GOALS = 'Your Goals';

/**
 * The review's sections, in step order: the test variant of the program-step
 * experiment reviews its "Program of Interest" step after Education; control
 * keeps the program inside Personal Information.
 */
export function reviewSections(programStepVariant = 'control') {
  return programStepVariant === 'test' ? [PERSONAL_INFORMATION, EDUCATION, PROGRAM_OF_INTEREST, DEMOGRAPHICS, YOUR_GOALS] : [PERSONAL_INFORMATION, EDUCATION, DEMOGRAPHICS, YOUR_GOALS];
}
function ApplicationFormReview(t0) {
  const $ = _c(32);
  const {
    site,
    terms,
    degrees,
    isSubmitting,
    email,
    passwordDisabled,
    employerDisabled,
    programStepVariant: t1,
    classTimesSaveFailed: t2
  } = t0;
  const programStepVariant = t1 === undefined ? "control" : t1;
  const classTimesSaveFailed = t2 === undefined ? false : t2;
  const [section, setSection] = useState(PERSONAL_INFORMATION);
  const ref = useRef();
  const sections = reviewSections(programStepVariant);
  const programOnOwnStep = programStepVariant === "test";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      const els = document.querySelectorAll("[data-section]");
      for (let i = els.length - 1; i >= 0; i--) {
        const domRect = els[i].getBoundingClientRect();
        const parentRect = e.target.getBoundingClientRect();
        if (domRect.y - parentRect.y - 1 <= 0) {
          setSection(els[i].dataset.section);
          break;
        }
      }
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleScroll = t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (ref.current) {
        ref.current.scrollTo({
          top: 24,
          behavior: "auto"
        });
      }
    };
    t5 = [];
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  let t6;
  if ($[3] !== classTimesSaveFailed || $[4] !== degrees || $[5] !== email || $[6] !== employerDisabled || $[7] !== passwordDisabled || $[8] !== programOnOwnStep || $[9] !== site || $[10] !== terms) {
    t6 = name => {
      switch (name) {
        case PERSONAL_INFORMATION:
          {
            return <PersonalInformation email={email} degrees={degrees} passwordDisabled={passwordDisabled} employerDisabled={employerDisabled} showProgram={!programOnOwnStep} />;
          }
        case EDUCATION:
          {
            return <Education terms={terms} autoFocus={false} />;
          }
        case PROGRAM_OF_INTEREST:
          {
            return <ProgramOfInterest degrees={degrees} site={site} autoFocus={false} saveFailed={classTimesSaveFailed} />;
          }
        case DEMOGRAPHICS:
          {
            return <Demographics autoFocus={false} />;
          }
        case YOUR_GOALS:
          {
            return <Goals autoFocus={false} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[3] = classTimesSaveFailed;
    $[4] = degrees;
    $[5] = email;
    $[6] = employerDisabled;
    $[7] = passwordDisabled;
    $[8] = programOnOwnStep;
    $[9] = site;
    $[10] = terms;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const renderSection = t6;
  const t7 = styles;
  let t8;
  if ($[12] !== section) {
    t8 = <div className={styles.review__title}>{section}</div>;
    $[12] = section;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = styles;
  let t10;
  if ($[14] !== renderSection) {
    t10 = name_1 => <div key={name_1} data-section={name_1}>{renderSection(name_1)}</div>;
    $[14] = renderSection;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = sections.map(t10);
  let t12;
  if ($[16] !== handleScroll || $[17] !== t11 || $[18] !== t9.review__panel) {
    t12 = <div ref={ref} className={t9.review__panel} onScroll={handleScroll}>{t11}</div>;
    $[16] = handleScroll;
    $[17] = t11;
    $[18] = t9.review__panel;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FieldChoiceGroup legend={null} legendHidden={true} appearance="list" name="consentToSms"><Field name="consentToSms" type="checkbox" label="By clicking this box, I agree to receive transactional and reminder SMS messages from Campus. Message frequency varies; reply STOP to opt out." /></FieldChoiceGroup>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className={styles.review__acknowledgements}>{t13}<FieldChoiceGroup legend={null} legendHidden={true} appearance="list" name="accurateInfoCheck"><Field name="accurateInfoCheck" type="checkbox" label="By submitting this application to Campus, I attest that the information provided is accurate." /></FieldChoiceGroup></div>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== isSubmitting) {
    t15 = isSubmitting ? <Spinner label="Submitting" /> : "Submit";
    $[22] = isSubmitting;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isSubmitting || $[25] !== t15) {
    t16 = <FormActions className={reviewStyles.actions}><Button type="submit" shape="rounded" fullWidth={true} disabled={isSubmitting}>{t15}</Button></FormActions>;
    $[24] = isSubmitting;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t12 || $[28] !== t16 || $[29] !== t7.review || $[30] !== t8) {
    t17 = <div className={t7.review}>{t8}{t12}{t14}{t16}</div>;
    $[27] = t12;
    $[28] = t16;
    $[29] = t7.review;
    $[30] = t8;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return <><ul className={styles.review__steps}>{sections.map(name_0 => <li key={name_0} className={classNames({
        [reviewStyles.active]: section === name_0
      })} role="button" tabIndex={0} onKeyDown={_temp} onClick={e_1 => {
        const el = document.querySelector(`[data-section="${name_0}"]`);
        ref.current.scrollTo({
          top: el.offsetTop,
          behavior: "smooth"
        });
      }}>{name_0}</li>)}</ul>{t17}</>;
}
function _temp(e_0) {
  if (e_0.key === "Enter" || e_0.key === " ") {
    e_0.currentTarget.click();
  }
}
export default ApplicationFormReview;